import { useQuery } from '@tanstack/react-query';

import { fetchBrands, fetchBrandVenues, MERCHANT_ENTERPRISE_SERVICE } from 'apis/enterprise/api';
import useFetchAllMerchantVenues from 'modules/merchant/hooks/useFetchAllMerchantVenues';

import { Entity } from '../types';

/**
 * A react hook to return the venue IDs for a merchant, brand or enterprise.
 */
const useVenueIds = (entity: Entity) => {
  const isEnableFetchMerchantVenueIds = entity.type === 'merchant';
  const merchantId = isEnableFetchMerchantVenueIds ? entity.merchant.itemid.$oid : '';
  const {
    venues: merchantVenues,
    isLoading: isLoadingMerchantVenues,
    error: fetchAllMerchantVenuesError,
  } = useFetchAllMerchantVenues(isEnableFetchMerchantVenueIds, merchantId);

  const isEnableFetchBrandVenues = entity.type === 'brand';
  const brandId = isEnableFetchBrandVenues ? entity.brand.id : '';
  const {
    data: brandVenues,
    isLoading: isLoadingBrandVenueIds,
    error: fetchBrandVenueIdsError,
  } = useQuery([`${MERCHANT_ENTERPRISE_SERVICE}/brands/${brandId}/venues`], () => fetchBrandVenues(brandId), {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: isEnableFetchBrandVenues && Boolean(brandId),
  });

  const isEnableFetchEnterpriseVenues = entity.type === 'enterprise';
  const enterpriseId = isEnableFetchEnterpriseVenues ? entity.enterprise.id : '';
  const {
    data: enterpriseVenues,
    isLoading: isLoadingEnterpriseVenueIds,
    error: fetchEnterpriseVenueIdsError,
  } = useQuery(
    [`${MERCHANT_ENTERPRISE_SERVICE}/enterprises/${enterpriseId}/venues`],
    () => fetchEnterpriseVenues(enterpriseId),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isEnableFetchEnterpriseVenues && Boolean(enterpriseId),
    },
  );

  switch (entity.type) {
    case 'merchant':
      return {
        isLoading: isLoadingMerchantVenues,
        venueIds: merchantVenues.map(item => item.id),
        error: fetchAllMerchantVenuesError,
      };
    case 'brand':
      return {
        isLoading: isLoadingBrandVenueIds,
        venueIds: brandVenues?.map(brandVenue => brandVenue.venueId) ?? [],
        error: fetchBrandVenueIdsError,
      };
    case 'enterprise':
      return {
        isLoading: isLoadingEnterpriseVenueIds,
        venueIds: enterpriseVenues?.map(enterpriseVenue => enterpriseVenue.venueId) ?? [],
        error: fetchEnterpriseVenueIdsError,
      };
    case 'venue-group':
      return {
        venueIds: entity.venueGroup.venueIds || [],
      };
    default:
      throw new Error('useVenueIds only takes `merchant`, `brand` or `enterprise` entity type!');
  }
};

const fetchEnterpriseVenues = async (enterpriseId: string) => {
  const brandItems = await fetchBrands(enterpriseId);
  const brandVenuesList = await Promise.all(brandItems.map(brandItem => fetchBrandVenues(brandItem.brand.id)));

  return brandVenuesList.flat();
};

export default useVenueIds;
